import React, {Suspense, useContext} from "react";
import {Route} from "react-router";
import {Navigate, Routes} from "react-router-dom";
import Layout from "./layouts/Layout";
import Loading from "./components/Loading";
import {UserSettingsResetPassword} from "./components/UserSettingsResetPassword";
import {UserSettingsSupport} from "./components/UserSettingsSupport";
import {UserSettingsPersonalVehicles} from "./components/UserSettingsPersonalVehicles";
import {UserSettingsOther} from "./components/UserSettingsOther";
import {UserSettingsDocuments} from "./components/UserSettingsDocuments";
import {UserSettingsExternalAccounts} from "./components/UserSettingsExternalAccounts";
import {UserSettingsMcpApiKeys} from "./components/UserSettingsMcpApiKeys";
import {UserSettingsProfile} from "./components/UserSettingsProfile";
import {CompanySQL} from "./components/CompanySQL";
import {WikiLayout} from "./layouts/WikiLayout";
import {IWikiParams} from "./interfaces/IWikiParams";
import {AdminEditCompanySettingsSection} from "./pages/AdminEditCompanySettingsSection";
import PantheonUpgradeLogsPage from "./pages/PantheonUpgradeLogsPage";
import {AresDeploymentsPage} from "./pages/AresDeploymentsPage";
import {AuthContext} from "./contexts/AuthContext";
import {UserInfoHelper} from "./helpers/UserInfoHelper";
import {OnixRoleEnum} from "./enums/OnixRoleEnum";
import {buildAresDeploymentGroupInstalledCompaniesRoute} from "./helpers/AresDeploymentRouteHelpers";

// LAZY LOADED PAGES
const CompanyUsers = React.lazy(() => import("./pages/CompanyUsers"));
const SupportTicketPage = React.lazy(() => import("./pages/SupportTicketPage"));
const FiscalizationsPage = React.lazy(
    () => import("./pages/FiscalizationsPage"),
);
const WikiMainPublicPage = React.lazy(
    () => import("./pages/WikiMainPublicPage"),
);
const IBackupEntriesPage = React.lazy(
    () => import("./pages/IBackupEntriesPage"),
);
const IBackupComputersPage = React.lazy(
    () => import("./pages/IBackupComputersPage"),
);
const WikiEntryAdminPage = React.lazy(
    () => import("./pages/WikiEntryAdminPage"),
);
const WikiEntryOverviewPage = React.lazy(
    () => import("./pages/WikiEntryOverviewPage"),
);
const WikiEntryTagsAdminPage = React.lazy(
    () => import("./pages/WikiEntryTagsAdminPage"),
);
const WikiEntryCategoriesAdminPage = React.lazy(
    () => import("./pages/WikiEntryCategoriesAdminPage"),
);
const AllCompaniesSQLPage = React.lazy(
    () => import("./pages/AllCompaniesSQLPage"),
);
const TravelWarrants2OverviewPage = React.lazy(
    () => import("./pages/TravelWarrants2OverviewPage"),
);
const ClientServersPage = React.lazy(() => import("./pages/ClientServersPage"));
const SqlBackupOverviewPage = React.lazy(() => import("./pages/SqlBackupOverviewPage"));
const OnixClrInstallationsPage = React.lazy(
    () => import("./pages/OnixClrInstallationsPage"),
);
const OnixClrUsageReportPage = React.lazy(
    () => import("./pages/OnixClrUsageReportPage"),
);
const OnixDmsBillingReportPage = React.lazy(
    () => import("./pages/OnixDmsBillingReportPage"),
);

const PantheonDocsPage = React.lazy(
    () => import("./pages/PantheonDocsPage"),
);
const AdminAiKnowledgePage = React.lazy(
    () => import("./pages/AdminAiKnowledgePage"),
);
const BobAiPage = React.lazy(
    () => import("./pages/BobAiPage"),
);
const AresActivityReportPage = React.lazy(
    () => import("./pages/AresActivityReportPage"),
);
const OnixPhoneFunctionalityActivityReportPage = React.lazy(
    () => import("./pages/OnixPhoneFunctionalityActivityReportPage"),
);
const DatalabMappingsPage = React.lazy(
    () => import("./pages/DatalabMappingsPage"),
);

const SupportPage = React.lazy(() => import("./pages/SupportTicketsPage"));
const WorkRecords = React.lazy(() => import("./pages/WorkRecordsPage"));
const TenantUsers = React.lazy(() => import("./pages/TenantUsersPage"));
const CompanySettings = React.lazy(
    () => import("./pages/TenantCompanySettingsPage"),
);
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const ConfirmEmailPage = React.lazy(() => import("./pages/ConfirmEmailPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const AdminUsersPage = React.lazy(() => import("./pages/AdminUsersPage"));
const AdminEditUserPage = React.lazy(() => import("./pages/AdminEditUserPage"));
const AdminCompaniesPage = React.lazy(
    () => import("./pages/AdminCompaniesPage"),
);
const UserSettingsPage = React.lazy(() => import("./pages/UserSettingsPage"));
const MimicUserPage = React.lazy(() => import("./pages/MimicUserPage"));
const AdminEditCompanyPage = React.lazy(
    () => import("./pages/AdminEditCompanyPage"),
);
const WorkRecordOverviewPage = React.lazy(
    () => import("./pages/WorkRecordOverviewPage"),
);
const WorkRecordsFinalizePage = React.lazy(
    () => import("./pages/WorkRecordsFinalizePage"),
);
const WorkRecordsUploadPage = React.lazy(
    () => import("./pages/WorkRecordsUploadPage"),
);
const TenantEditUserPage = React.lazy(
    () => import("./pages/TenantEditUserPage"),
);
const SupportNewTicketPage = React.lazy(
    () => import("./pages/SupportNewTicketPage"),
);
const TravelWarrantsPage = React.lazy(
    () => import("./pages/TravelWarrantsPage"),
);
const CodebooksCompaniesPage = React.lazy(
    () => import("./pages/CodebooksCompaniesPage"),
);
const CodebooksContactPage = React.lazy(
    () => import("./pages/CodebooksContactPage"),
);
const ContactOverviewPage = React.lazy(
    () => import("./pages/ContactOverviewPage"),
);
const TravelWarrantPage = React.lazy(() => import("./pages/TravelWarrantPage"));
const SupportDashboardPage = React.lazy(
    () => import("./pages/SupportDashboardPage"),
);
const PreparedSupportTimeLogsPage = React.lazy(
    () => import("./pages/PreparedSupportTimeLogsPage"),
);
const SupportCannedResponsesPage = React.lazy(
    () => import("./pages/SupportCannedResponsesPage"),
);
const SupportGroupsPage = React.lazy(
    () => import("./pages/SupportGroupsPage"),
);
const SupportDailyMetricsPage = React.lazy(
    () => import("./pages/SupportDailyMetricsPage"),
);
const SupportDailyMetricsDetailsPage = React.lazy(
    () => import("./pages/SupportDailyMetricsDetailsPage"),
);
const SupportAbsencesPage = React.lazy(
    () => import("./pages/SupportAbsencesPage"),
);
const CodebooksProjectsPage = React.lazy(
    () => import("./pages/CodebooksProjectsPage"),
);
const CompanyOverviewPage = React.lazy(
    () => import("./pages/CompanyOverviewPage"),
);
const ProjectOverviewPage = React.lazy(
    () => import("./pages/ProjectOverviewPage"),
);
const ToDosPage = React.lazy(() => import("./pages/ToDosPage"));
const TasksPage = React.lazy(() => import("./pages/TasksPage"));
const MarservisWorkOrdersPage = React.lazy(
    () => import("./pages/MarservisWorkOrdersPage"),
);
const CodebooksCustomersPage = React.lazy(
    () => import("./pages/CodebooksCustomersPage"),
);
const CodebooksItemsPage = React.lazy(
    () => import("./pages/CodebooksItemsPage"),
);
const CustomerOverviewPage = React.lazy(
    () => import("./pages/CustomerOverviewPage"),
);
const UserDocumentTypesPage = React.lazy(
    () => import("./pages/UserDocumentTypesPage"),
);
const MarservisUserWorkOrdersPage = React.lazy(
    () => import("./pages/MarservisUserWorkOrdersPage"),
);
const CodebooksDepartmentsPage = React.lazy(
    () => import("./pages/CodebooksDepartmentsPage"),
);
const CodebooksItemGroupsPage = React.lazy(
    () => import("./pages/CodebooksItemGroupsPage"),
);
const MarservisMaterialOverviewPage = React.lazy(
    () => import("./pages/MarservisMaterialOverviewPage"),
);
const ItemGroupOverviewPage = React.lazy(
    () => import("./pages/ItemGroupOverviewPage"),
);
const DepartmentOverviewPage = React.lazy(
    () => import("./pages/DepartmentOverviewPage"),
);
const ItemOverviewPage = React.lazy(() => import("./pages/ItemOverviewPage"));
const TeamViewerSessionsPage = React.lazy(
    () => import("./pages/TeamViewerSessionsPage"),
);
const TeamViewerSessionOverviewPage = React.lazy(
    () => import("./pages/TeamViewerSessionOverviewPage"),
);
const WakaTimeWorklogsPage = React.lazy(
    () => import("./pages/WakaTimeWorklogsPage"),
);
const WakaTimeWorklogOverviewPage = React.lazy(
    () => import("./pages/WakaTimeWorklogOverviewPage"),
);
const AdminEditUserDocumentFlow = React.lazy(
    () => import("./pages/AdminEditUserDocumentFlowPage"),
);
const AdminEditUserDocumentLinksFlow = React.lazy(
    () => import("./pages/AdminEditUserDocumentLinksFlowPage"),
);
const UserNewDocumentTypePairPage = React.lazy(
    () => import("./pages/UserDocumentTypePairsPage"),
);
const MarservisWorkOrdersOverviewPage = React.lazy(
    () => import("./pages/MarservisWorkOrdersOverviewPage"),
);
const MarservisUserWorkOrderGroupItemsPage = React.lazy(
    () => import("./pages/MarservisUserWorkOrderGroupItemsPage"),
);
const MarservisWorkOrderItemWorksPage = React.lazy(
    () => import("./pages/MarservisWorkOrderItemWorksPage"),
);
const SwitchEnvironmentPage = React.lazy(
    () => import("./pages/SwitchEnvironmentPage"),
);
const OnixPhoneCompaniesOverviewPage = React.lazy(
    () => import("./pages/OnixPhoneCompaniesOverviewPage"),
);
const WorkOrderItemsAdditionalReportDataEditorPage = React.lazy(
    () => import("./pages/WorkOrderItemsAdditionalReportDataEditorPage"),
);
const OnixPhoneLoginQrCodePage = React.lazy(
    () => import("./pages/OnixPhoneLoginQrCodePage"),
);
const PantheonSubjectsPage = React.lazy(
    () => import("./pages/PantheonSubjectsPage"),
);
// WEB DOCUMENTS
const WebDocumentNewMovePage = React.lazy(
    () => import("./pages/WebDocumentNewMovePage"),
);
const WebDocumentNewOrderPage = React.lazy(
    () => import("./pages/WebDocumentNewOrderPage"),
);
const WebDocumentMoveListPage = React.lazy(
    () => import("./pages/WebDocumentMoveListPage"),
);
const WebDocumentOrderListPage = React.lazy(
    () => import("./pages/WebDocumentOrderListPage"),
);
const WebDocumentTMnListPage = React.lazy(
    () => import("./pages/WebDocumentTMnListPage"),
);
const SendBulkEmailPage = React.lazy(() => import("./pages/SendBulkEmailPage"));
const SearchTicketMessagesByContentPage = React.lazy(
    () => import("./pages/SearchTicketMessagesByContent"),
);
const CompanyPreparednessPage = React.lazy(
    () => import("./pages/CompanyPreparednessPage"),
);
const AresPage = React.lazy(() => import("./pages/AresPage"));
const PlainSQLTestingPage = React.lazy(
    () => import("./pages/PlainSQLTestingPage"),
);
const CompanySqlTasksPage = React.lazy(
    () => import("./pages/CompanySqlTasksPage"),
);
const SavedFilesPage = React.lazy(() => import("./pages/SavedFilesPage"));
const CachesPage = React.lazy(() => import("./pages/CachesPage"));
const LabelDefinitionsPage = React.lazy(
    () => import("./pages/LabelDefinitionsPage"),
);
const PantheonInisPage = React.lazy(() => import("./pages/PantheonInisPage"));
const PantheonPage = React.lazy(() => import("./pages/PantheonPage"));
const UpdatePantheonIniFromLocalPage = React.lazy(
    () => import("./pages/UpdatePantheonIniFromLocalPage"),
);
const OnixPhoneUserTasksPage = React.lazy(
    () => import("./pages/OnixPhoneUserTasksPage"),
);
const CodebooksTimeLogTemplates = React.lazy(
    () => import("./pages/CodebooksTimeLogTemplates"),
);
const EmailSettingsTestingPage = React.lazy(
    () => import("./pages/EmailSettingsTestingPage"),
);
const EmailSettingsTestingOverviewPage = React.lazy(
    () => import("./pages/EmailSettingsTestingOverviewPage"),
);
const AdminDocumentTypeTemplatesPage = React.lazy(
    () => import("./pages/AdminDocumentTypeTemplatesPage"),
);
const AresDeploymentGroupSnapshotsPage = React.lazy(
    () => import("./pages/AresDeploymentGroupSnapshotsPage"),
);
const AresDeploymentGroupInstalledCompaniesPage = React.lazy(
    () => import("./pages/AresDeploymentGroupInstalledCompaniesPage"),
);

export const AppRoute = {
    HOME: "/",
    DASHBOARD: "/dashboard",
    LOGIN: "/login",
    FORGOT_PASSWORD: "/forgot-password",
    RESET_PASSWORD: "/reset-password",
    CONFIRM_EMAIL: "/confirm-email",

    WIKI_ENTRY_TAGS_ADMIN: "/wiki-entry-tags-admin",

    ADMIN_USERS: "/users",
    HANGFIRE_DASHBOARD: "/hangfire",
    WEB_APP: "/app/",
    RPORT: "https://rport.onix-pu.hr",
    SWITCH_ENVIRONMENT: "/switch-environment",
    SWAGGER: "/swagger",
    SUPPORT_TODOS: "/support-todos",
    SUPPORT_TASKS: "/support-tasks",
    CODEBOOKS_CUSTOMERS: "/codebooks-customers",
    CODEBOOKS_ITEMS: "/codebooks-items",
    MARSERVIS_WORK_ORDERS: "/marservis-work-orders",
    GRAPHQL: "/graphql",
    MARSERVIS_USER_WORK_ORDERS: "/marservis-user-work-orders",
    CODEBOOKS_DEPARTMENTS: "/codebooks-departments",
    CODEBOOKS_ITEM_GROUPS: "/codebooks-item-groups",
    CODEBOOKS_MARSERVIS_MATERIALS: "/codebooks-marservis-materials",
    ONIX_PHONE_LOGIN_QRCODE: "/onix-phone-login-qrcode",
    ARES_ACTIVITY_REPORT: "/ares-activity-report",
    ONIX_CLR_USAGE_REPORT: "/onix-clr-usage-report",
    ONIX_DMS_BILLING_REPORT: "/onix-dms-billing-report",
    ONIX_PHONE_FUNCTIONALITY_ACTIVITY_REPORT: "/onix-phone-functionality-activity-report",
    ARES_DEPLOYMENT_GROUP_SNAPSHOTS: (groupId?: number) =>
        "/ares-deployments/snapshots" + (groupId ? `?groupId=${groupId}` : ""),

    WIKI_ENTRY_ADMIN: "/wiki-entry-admin",
    WIKI_ENTRY_CATEGORIES_ADMIN: "/wiki-entry-categories-admin",
    IBACKUP_ENTRIES: "/ibackup-entries",
    IBACKUP_COMPUTERS: "/ibackup-computers",
    SQL_BACKUP_OVERVIEW: "/sql-backup",
    TRAVEL_WARRANTS2_OVERVIEW: "/travel-warrants2/overview",

    WIKI_PUBLIC: (data?: IWikiParams) =>
        `/Wiki${data?.entryId ? `?entryId=${data?.entryId}` : data?.categoryId ? `?categoryId=${data?.categoryId}` : data?.tagId ? `?tagId=${data?.tagId}` : ""}`,

    WIKI_ENTRY_OVERVIEW: (id?: number) =>
        "/wiki-entry-overview" + (id ? `?id=${id}` : ""),

    ADMIN_EDIT_USER_DOCUMENT_FLOW: (id?: string) =>
        "/users/document-flow" + (id ? `?id=${id}` : ""),
    ADMIN_EDIT_USER_DOCUMENT_LINKS_FLOW: (id?: string) =>
        "/users/document-flow-links" + (id ? `?id=${id}` : ""),

    ADMIN_EDIT_USER_DOCUMENT_TRANSFORMATIONS: (id?: number) =>
        "/users/document_transformations" + (id ? `?id=${id}` : ""),

    ADMIN_EDIT_USER_DOCUMENT_TYPES: (id?: number) =>
        "/users/document_types" + (id ? `?id=${id}` : ""),

    ADMIN_DOCUMENT_TYPE_TEMPLATES: "/document-type-templates",

    ADMIN_EDIT_USER: (id?: string) => "/users/edit" + (id ? `?id=${id}` : ""),

    ADMIN_COMPANIES: "/companies",

    SUPPORT_TIME_LOGS: "/support-dashboard",
    PREPARED_SUPPORT_TIME_LOGS: "/prepared-support-timelogs-dashboard",
    NEW_SUPPORT_TICKET: "/support/new-ticket",
    EDIT_SUPPORT_TICKET: (id?: number, detail?: number) =>
        "/support/edit" +
        (id ? `?id=${id}` + (detail ? `#ticket-message-${detail}` : "") : ""),
    SUPPORT: "/support",
    SUPPORT_DAILY_METRICS: "/support/daily-metrics",
    SUPPORT_DAILY_METRICS_DAY: (day?: string) =>
        `/support/daily-metrics/${day ?? ":day"}`,
    SUPPORT_ABSENCES: "/support/absences",
    SUPPORT_CANNED_RESPONSES: "/support/canned-responses",
    SUPPORT_GROUPS: "/support/groups",
    SUPPORT_BOB: "/support/bob",
    ONIX_CLR_INSTALLATIONS: "/support/onix-clr-installations",
    PANTHEON_DOCS: "/support/pantheon-docs",
    DATALAB_MAPPINGS: "/support/datalab-mappings",
    AI_KNOWLEDGE: "/administration/ai-knowledge",

    SETTINGS: "/settings",
    SETTINGS_RESET_PASSWORD: "/settings/reset-password",
    SETTINGS_PERSONAL_DATA: "/settings/personal-data",
    SETTINGS_PROFILE: "/settings/profile",
    SETTINGS_SUPPORT: "/settings/support",
    SETTINGS_PERSONAL_VEHICLES: "/settings/personal-vehicles",
    SETTINGS_OTHER: "/settings/other",
    SETTINGS_DOCUMENTS: "/settings/documents",
    SETTINGS_EXTERNAL_ACCOUNTS: "/settings/external-accounts",
    SETTINGS_MCP_API_KEYS: "/settings/mcp-api-keys",

    EDIT_COMPANY: (id?: number) => "/companies/edit/" + (id ? `${id}` : ":id"),
    EDIT_COMPANY_USERS: (id?: number) =>
        "/companies/edit/" + (id ? `${id}` : ":id") + "/users",
    EDIT_COMPANY_SQL_FORM: (id?: number) =>
        "/companies/edit/" + (id ? `${id}` : ":id") + "/sql-form",
    ALL_COMPANIES_SQL_FORM: "/companies/sql-form",
    EDIT_COMPANY_PREPAREDNESS: (id?: number) =>
        `/companies/edit/${id ? `${id}` : ":id"}/preparedness`,

    MIMIC: (token?: string) => "/mimic" + (token ? `?token=${token}` : ""),

    COMPANY_USERS: "/company/users",
    EDIT_COMPANY_USER: (id?: string) =>
        "/company/users/edit" + (id ? `?id=${id}` : ""),

    COMPANY_SETTINGS: "/company/settings",

    FISCALIZATIONS: "/fiscalizations",

    WORK_RECORDS: (userId?: string, year?: number, month?: number) =>
        "/work-records" +
        (userId ? `?userId=${userId}&year=${year}&month=${month}` : ""),
    WORK_RECORDS_OVERVIEW: (tab?: string) =>
        "/work-records-overview" + (tab ? `?tab=${tab}` : ""),
    WORK_RECORDS_FINALIZE: "/work-records/finalize",
    WORK_RECORDS_UPLOAD: "/work-records/upload",

    CODEBOOKS_CONTACTS: "/codebooks-contacts",
    CODEBOOKS_COMPANIES: "/codebooks-companies",
    CODEBOOKS_PROJECTS: "/codebooks-projects",
    CONTACT_OVERVIEW: (id?: number) =>
        "/codebooks-contacts/edit" + (id ? `?id=${id}` : ""),
    COMPANY_OVERVIEW: (id?: number) =>
        "/codebooks-companies/edit" + (id ? `?id=${id}` : ""),
    PROJECT_OVERVIEW: (id?: number) =>
        "/codebooks-projects/edit" + (id ? `?id=${id}` : ""),

    TRAVEL_WARRANT: (id?: string) =>
        "/travel-warrants/edit" + (id ? `?id=${id}` : ""),
    TRAVEL_WARRANTS: "/travel-warrants",

    WEBSHOP_ARTICLES: "/webshop/articles",
    WEBSHOP_FEATURED_CATEGORIES: "/webshop/featured-categories",
    CUSTOMER_OVERVIEW: (id?: number) =>
        "/codebooks-customers/edit" + (id ? `?id=${id}` : ""),

    SPAM_EMAILS: "/spam-emails",
    ITEM_GROUP_OVERVIEW: (id?: number) =>
        "/codebooks-item-groups/edit" + (id ? `?id=${id}` : ""),
    DEPARTMENT_OVERVIEW: (id?: number) =>
        "/codebooks-departments/edit" + (id ? `?id=${id}` : ""),
    ITEM_OVERVIEW: (id?: number) =>
        "/codebooks-items/edit" + (id ? `?id=${id}` : ""),
    TEAM_VIEWER_SESSIONS: "/team-viewer-sessions",
    TEAM_VIEWER_SESSION: (id?: string) =>
        "/team-viewer-session/edit" + (id ? `?id=${id}` : ""),
    WAKA_TIME_WORKLOGS: "/waka-time-worklogs",
    WAKA_TIME_WORKLOG: (id?: number) =>
        "/waka-time-worklog/edit" + (id ? `?id=${id}` : ""),
    HEALTHCHECKS_DASHBOARD: "https://onixwebapphealthchecks.azurewebsites.net",
    MARSERVIS_WORK_ORDERS_OVERVIEW: "/marservis-work-orders/overview",
    MARSERVIS_WORK_ORDER_ITEMS: (id?: string) =>
        "/marservis-work-order-items" + (id ? `?id=${id}` : ""),

    MARSERVIS_USER_WORK_ORDER_GROUP_ITEMS: (id?: number) =>
        "/marservis-user-work-order-group-items" + (id ? `?id=${id}` : ""),
    MARSERVIS_WORK_ORDER_ITEM_WORKS: (id?: number) =>
        "/marservis-work-order-item-works" + (id ? `?id=${id}` : ""),
    WorkOrderItemsAdditionalReportDataEditor: (companyId?: number) =>
        "/WorkOrderItemsAdditionalReportDataEditor" +
        (companyId ? `?companyId=${companyId}` : ""),
    CODEBOOKS_PANTHEON_SUBJECTS: (forVersion?: string) => forVersion ? "/codebooks-pantheon-subjects?forVersion=" + forVersion : "/codebooks-pantheon-subjects",

    ARES: "/ares",
    SQL_TASKS: "/sql-tasks",

    // WEB DOCUMENTS

    WEB_DOCUMENT_MOVE_LIST: (docId?: number) =>
        `/web-documents-move/${docId !== undefined ? docId : ":docId"}`,
    WEB_DOCUMENT_ORDER_LIST: (docId?: number) =>
        `/web-documents-order/${docId !== undefined ? docId : ":docId"}`,
    WEB_DOCUMENT_TMN_LIST: (docId?: number) =>
        `/web-documents-tmn/${docId !== undefined ? docId : ":docId"}`,
    WEB_DOCUMENT_NEW_MOVE: (docId?: number, id?: string) =>
        `/web-documents-move/${docId !== undefined ? docId : ":docId"}/new${
            id ? `?id=${id}` : ""
        }`,
    WEB_DOCUMENT_NEW_ORDER: (docId?: number, id?: string) =>
        `/web-documents-order/${docId !== undefined ? docId : ":docId"}/new${
            id ? `?id=${id}` : ""
        }`,

    SEND_BULK_EMAIL: "/send-bulk-email",
    SEARCH_TICKET_MESSAGES_BY_CONTENT: "/search-ticket-messages-by-content",
    PLAIN_SQL_TESTING: "/plain-sql-testing",

    SAVED_FILES: "/saved-files",
    CACHES: "/caches",
    LABEL_DEFINITIONS: "/label-definitions",

    PANTHEON_INIS: "/pantheon-inis",
    PANTHEON: "/pantheon",
    UPDATE_PANTHEON_INI_FROM_LOCAL: "/update-pantheon-ini-from-local",
    ONIX_PHONE_USER_TASKS: "/onix-phone-user-tasks",
    CODEBOOKS_TIME_LOG_TEMPLATES: "/codebooks-time-log-templates",
    EMAIL_SETTINGS_TESTING: "/email-settings-testing",
    EMAIL_SETTINGS_TESTING_OVERVIEW: (id?: number) =>
        `/email-settings-testing/edit${id ? `?id=${id}` : ""}`,
    CLIENT_SERVERS: "/client-servers",
    PANTHEON_UPGRADE_LOGS: "/pantheon-upgrade-logs",
    ARES_DEPLOYMENTS: "/ares-deployments",
    ARES_DEPLOYMENT_GROUP_INSTALLED_COMPANIES: buildAresDeploymentGroupInstalledCompaniesRoute,
    ONIX_PHONE_COMPANIES_OVERVIEW: "/support/onixphone-companies-overview",
};

export const CompiledRoutes = Object.keys(AppRoute).map((routeKey) => {
    const routeValue = (AppRoute as any)[routeKey] as any;
    return {
        routeKey,
        routeValue: typeof routeValue === "string" ? routeValue : routeValue(),
    };
});

export const AppRoutes = () => {
    const {UserInfo} = useContext(AuthContext);
    const supportOnly = UserInfoHelper.HasRole(
        OnixRoleEnum.GRUNF_SUPPORT,
        UserInfo,
    );

    if (supportOnly) {
        return (
            <Suspense fallback={<Loading/>}>
                <Routes>
                    <Route path={AppRoute.LOGIN} element={<LoginPage/>}/>
                    <Route path={AppRoute.FORGOT_PASSWORD} element={<ForgotPasswordPage/>}/>
                    <Route path={AppRoute.RESET_PASSWORD} element={<ResetPasswordPage/>}/>
                    <Route path={AppRoute.CONFIRM_EMAIL} element={<ConfirmEmailPage/>}/>
                    <Route
                        path={AppRoute.SWITCH_ENVIRONMENT}
                        element={<SwitchEnvironmentPage/>}
                    />
                    <Route
                        path={AppRoute.HOME}
                        element={<Navigate to={AppRoute.SUPPORT}/>}
                    />
                    <Route element={<Layout/>}>
                        <Route path={AppRoute.SUPPORT} element={<SupportPage/>}/>
                        <Route
                            path={AppRoute.SUPPORT_DAILY_METRICS}
                            element={<SupportDailyMetricsPage/>}
                        />
                        <Route
                            path={AppRoute.SUPPORT_DAILY_METRICS_DAY()}
                            element={<SupportDailyMetricsDetailsPage/>}
                        />
                        <Route
                            path={AppRoute.SUPPORT_ABSENCES}
                            element={<SupportAbsencesPage/>}
                        />
                        <Route
                            path={AppRoute.NEW_SUPPORT_TICKET}
                            element={<SupportNewTicketPage/>}
                        />
                        <Route
                            path={AppRoute.EDIT_SUPPORT_TICKET()}
                            element={<SupportTicketPage/>}
                        />
                        <Route
                            path={AppRoute.SUPPORT_TIME_LOGS}
                            element={<SupportDashboardPage/>}
                        />
                        <Route
                            path={AppRoute.PREPARED_SUPPORT_TIME_LOGS}
                            element={<PreparedSupportTimeLogsPage/>}
                        />
                        <Route
                            path={AppRoute.SUPPORT_CANNED_RESPONSES}
                            element={<SupportCannedResponsesPage/>}
                        />
                        <Route
                            path={AppRoute.SUPPORT_GROUPS}
                            element={<SupportGroupsPage/>}
                        />
                        <Route
                            path={AppRoute.SUPPORT_BOB}
                            element={<BobAiPage/>}
                        />
                        <Route
                            path={AppRoute.WAKA_TIME_WORKLOGS}
                            element={<WakaTimeWorklogsPage/>}
                        />
                        <Route
                            path={AppRoute.WAKA_TIME_WORKLOG()}
                            element={<WakaTimeWorklogOverviewPage/>}
                        />
                        <Route
                            path={AppRoute.ONIX_CLR_INSTALLATIONS}
                            element={<OnixClrInstallationsPage/>}
                        />
                        <Route path={AppRoute.SETTINGS} element={<UserSettingsPage/>}>
                            <Route path={AppRoute.SETTINGS_PROFILE} element={<UserSettingsProfile/>}/>
                            <Route
                                path={AppRoute.SETTINGS}
                                element={<UserSettingsResetPassword/>}
                            />
                            <Route
                                path={AppRoute.SETTINGS_EXTERNAL_ACCOUNTS}
                                element={<UserSettingsExternalAccounts/>}
                            />
                            <Route
                                path={AppRoute.SETTINGS_MCP_API_KEYS}
                                element={<UserSettingsMcpApiKeys/>}
                            />
                            <Route
                                path={AppRoute.SETTINGS_SUPPORT}
                                element={<UserSettingsSupport/>}
                            />
                            <Route
                                path={AppRoute.SETTINGS_OTHER}
                                element={<UserSettingsOther/>}
                            />
                        </Route>

                        <Route
                            path={AppRoute.PANTHEON_DOCS}
                            element={<PantheonDocsPage/>}
                        />
                        <Route
                            path={AppRoute.DATALAB_MAPPINGS}
                            element={<DatalabMappingsPage/>}
                        />
                        <Route
                            path={AppRoute.SUPPORT_TODOS}
                            element={<ToDosPage/>}
                        />
                        <Route path={AppRoute.SUPPORT_TASKS} element={<TasksPage/>}/>
                        <Route
                            path={AppRoute.SEARCH_TICKET_MESSAGES_BY_CONTENT}
                            element={<SearchTicketMessagesByContentPage/>}
                        />
                        <Route path="*" element={<Navigate to={AppRoute.SUPPORT}/>}/>
                    </Route>
                    <Route path="*" element={<Navigate to={AppRoute.SUPPORT}/>}/>
                </Routes>
            </Suspense>
        );
    }

    return (
        <Suspense fallback={<Loading/>}>
            <Routes>
                <Route path={AppRoute.LOGIN} element={<LoginPage/>}/>
                <Route path={AppRoute.FORGOT_PASSWORD} element={<ForgotPasswordPage/>}/>
                <Route path={AppRoute.RESET_PASSWORD} element={<ResetPasswordPage/>}/>
                <Route path={AppRoute.CONFIRM_EMAIL} element={<ConfirmEmailPage/>}/>
                <Route
                    path={AppRoute.SWITCH_ENVIRONMENT}
                    element={<SwitchEnvironmentPage/>}
                />
                <Route
                    path={AppRoute.HOME}
                    element={<Navigate to={AppRoute.DASHBOARD}/>}
                />
                <Route element={<Layout/>}>
                    <Route path={AppRoute.DASHBOARD} element={<DashboardPage/>}/>
                    <Route path={AppRoute.ADMIN_USERS} element={<AdminUsersPage/>}/>
                    <Route
                        path={AppRoute.ADMIN_EDIT_USER()}
                        element={<AdminEditUserPage/>}
                    />
                    <Route path={AppRoute.SETTINGS} element={<UserSettingsPage/>}>
                        <Route path={AppRoute.SETTINGS_PROFILE} element={<UserSettingsProfile/>}/>
                        <Route
                            path={AppRoute.SETTINGS}
                            element={<UserSettingsResetPassword/>}
                        />
                        <Route
                            path={AppRoute.SETTINGS_SUPPORT}
                            element={<UserSettingsSupport/>}
                        />
                        <Route
                            path={AppRoute.SETTINGS_PERSONAL_VEHICLES}
                            element={<UserSettingsPersonalVehicles/>}
                        />
                        <Route
                            path={AppRoute.SETTINGS_OTHER}
                            element={<UserSettingsOther/>}
                        />
                        <Route
                            path={AppRoute.SETTINGS_EXTERNAL_ACCOUNTS}
                            element={<UserSettingsExternalAccounts/>}
                        />
                        <Route
                            path={AppRoute.SETTINGS_MCP_API_KEYS}
                            element={<UserSettingsMcpApiKeys/>}
                        />
                        <Route
                            path={AppRoute.SETTINGS_DOCUMENTS}
                            element={<UserSettingsDocuments/>}
                        />
                    </Route>
                    <Route
                        path={AppRoute.ADMIN_COMPANIES}
                        element={<AdminCompaniesPage/>}
                    />
                    <Route
                        path={AppRoute.EDIT_COMPANY()}
                        element={<AdminEditCompanyPage/>}
                    >
                        <Route
                            path={AppRoute.EDIT_COMPANY()}
                            element={<AdminEditCompanySettingsSection/>}
                        />
                        <Route
                            path={AppRoute.EDIT_COMPANY_USERS()}
                            element={<CompanyUsers/>}
                        />
                        <Route
                            path={AppRoute.EDIT_COMPANY_SQL_FORM()}
                            element={<CompanySQL/>}
                        />
                        <Route
                            path={AppRoute.EDIT_COMPANY_PREPAREDNESS()}
                            element={<CompanyPreparednessPage/>}
                        />
                    </Route>
                    <Route path={AppRoute.MIMIC()} element={<MimicUserPage/>}/>
                    <Route path={AppRoute.WORK_RECORDS()} element={<WorkRecords/>}/>
                    <Route path={AppRoute.COMPANY_USERS} element={<TenantUsers/>}/>
                    <Route
                        path={AppRoute.COMPANY_SETTINGS}
                        element={<CompanySettings/>}
                    />
                    <Route
                        path={AppRoute.WORK_RECORDS_OVERVIEW()}
                        element={<WorkRecordOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.WORK_RECORDS_FINALIZE}
                        element={<WorkRecordsFinalizePage/>}
                    />
                    <Route
                        path={AppRoute.WORK_RECORDS_UPLOAD}
                        element={<WorkRecordsUploadPage/>}
                    />
                    <Route
                        path={AppRoute.EDIT_COMPANY_USER()}
                        element={<TenantEditUserPage/>}
                    />
                    <Route path={AppRoute.SUPPORT} element={<SupportPage/>}/>
                    <Route
                        path={AppRoute.DATALAB_MAPPINGS}
                        element={<DatalabMappingsPage/>}
                    />
                    <Route
                        path={AppRoute.SUPPORT_DAILY_METRICS}
                        element={<SupportDailyMetricsPage/>}
                    />
                    <Route
                        path={AppRoute.SUPPORT_DAILY_METRICS_DAY()}
                        element={<SupportDailyMetricsDetailsPage/>}
                    />
                    <Route
                        path={AppRoute.SUPPORT_ABSENCES}
                        element={<SupportAbsencesPage/>}
                    />
                    <Route
                        path={AppRoute.NEW_SUPPORT_TICKET}
                        element={<SupportNewTicketPage/>}
                    />
                    <Route
                        path={AppRoute.EDIT_SUPPORT_TICKET()}
                        element={<SupportTicketPage/>}
                    />
                    <Route
                        path={AppRoute.TRAVEL_WARRANTS}
                        element={<TravelWarrantsPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_COMPANIES}
                        element={<CodebooksCompaniesPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_CONTACTS}
                        element={<CodebooksContactPage/>}
                    />
                    <Route
                        path={AppRoute.CONTACT_OVERVIEW()}
                        element={<ContactOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.TRAVEL_WARRANT()}
                        element={<TravelWarrantPage/>}
                    />
                    <Route
                        path={AppRoute.SUPPORT_TIME_LOGS}
                        element={<SupportDashboardPage/>}
                    />
                    <Route
                        path={AppRoute.PREPARED_SUPPORT_TIME_LOGS}
                        element={<PreparedSupportTimeLogsPage/>}
                    />
                    <Route
                        path={AppRoute.SUPPORT_CANNED_RESPONSES}
                        element={<SupportCannedResponsesPage/>}
                    />
                    <Route
                        path={AppRoute.SUPPORT_GROUPS}
                        element={<SupportGroupsPage/>}
                    />
                    <Route
                        path={AppRoute.SUPPORT_BOB}
                        element={<BobAiPage/>}
                    />
                    <Route
                        path={AppRoute.ONIX_CLR_INSTALLATIONS}
                        element={<OnixClrInstallationsPage/>}
                    />

                    <Route
                        path={AppRoute.PANTHEON_DOCS}
                        element={<PantheonDocsPage/>}
                    />
                    <Route
                        path={AppRoute.AI_KNOWLEDGE}
                        element={<AdminAiKnowledgePage/>}
                    />
                    <Route
                        path={AppRoute.ONIX_PHONE_COMPANIES_OVERVIEW}
                        element={<OnixPhoneCompaniesOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_PROJECTS}
                        element={<CodebooksProjectsPage/>}
                    />
                    <Route
                        path={AppRoute.COMPANY_OVERVIEW()}
                        element={<CompanyOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.PROJECT_OVERVIEW()}
                        element={<ProjectOverviewPage/>}
                    />
                    <Route path={AppRoute.SUPPORT_TODOS} element={<ToDosPage/>}/>
                    <Route path={AppRoute.SUPPORT_TASKS} element={<TasksPage/>}/>
                    <Route
                        path={AppRoute.MARSERVIS_WORK_ORDERS}
                        element={<MarservisWorkOrdersPage/>}
                    />
                    <Route
                        path={AppRoute.MARSERVIS_WORK_ORDERS_OVERVIEW}
                        element={<MarservisWorkOrdersOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_CUSTOMERS}
                        element={<CodebooksCustomersPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_ITEMS}
                        element={<CodebooksItemsPage/>}
                    />
                    <Route
                        path={AppRoute.CUSTOMER_OVERVIEW()}
                        element={<CustomerOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.ADMIN_EDIT_USER_DOCUMENT_TYPES()}
                        element={<UserDocumentTypesPage/>}
                    />
                    <Route
                        path={AppRoute.MARSERVIS_USER_WORK_ORDERS}
                        element={<MarservisUserWorkOrdersPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_DEPARTMENTS}
                        element={<CodebooksDepartmentsPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_ITEM_GROUPS}
                        element={<CodebooksItemGroupsPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_MARSERVIS_MATERIALS}
                        element={<MarservisMaterialOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.ITEM_GROUP_OVERVIEW()}
                        element={<ItemGroupOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.DEPARTMENT_OVERVIEW()}
                        element={<DepartmentOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.ITEM_OVERVIEW()}
                        element={<ItemOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.TEAM_VIEWER_SESSIONS}
                        element={<TeamViewerSessionsPage/>}
                    />
                    <Route
                        path={AppRoute.TEAM_VIEWER_SESSION()}
                        element={<TeamViewerSessionOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.WAKA_TIME_WORKLOGS}
                        element={<WakaTimeWorklogsPage/>}
                    />
                    <Route
                        path={AppRoute.WAKA_TIME_WORKLOG()}
                        element={<WakaTimeWorklogOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.ADMIN_EDIT_USER_DOCUMENT_FLOW()}
                        element={<AdminEditUserDocumentFlow/>}
                    />
                    <Route
                        path={AppRoute.ADMIN_DOCUMENT_TYPE_TEMPLATES}
                        element={<AdminDocumentTypeTemplatesPage/>}
                    />
                    <Route
                        path={AppRoute.ADMIN_EDIT_USER_DOCUMENT_TRANSFORMATIONS()}
                        element={<UserNewDocumentTypePairPage/>}
                    />
                    <Route
                        path={AppRoute.ADMIN_EDIT_USER_DOCUMENT_LINKS_FLOW()}
                        element={<AdminEditUserDocumentLinksFlow/>}
                    />
                    <Route
                        path={AppRoute.MARSERVIS_WORK_ORDER_ITEMS()}
                        element={<MarservisWorkOrderItemWorksPage/>}
                    />
                    <Route
                        path={AppRoute.MARSERVIS_USER_WORK_ORDER_GROUP_ITEMS()}
                        element={<MarservisUserWorkOrderGroupItemsPage/>}
                    />
                    <Route
                        path={AppRoute.WorkOrderItemsAdditionalReportDataEditor()}
                        element={<WorkOrderItemsAdditionalReportDataEditorPage/>}
                    />
                    <Route
                        path={AppRoute.ONIX_PHONE_LOGIN_QRCODE}
                        element={<OnixPhoneLoginQrCodePage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_PANTHEON_SUBJECTS()}
                        element={<PantheonSubjectsPage/>}
                    />
                    <Route
                        path={AppRoute.SEND_BULK_EMAIL}
                        element={<SendBulkEmailPage/>}
                    />
                    {/*<Route path="*" element={<NotFoundPage/>}/>*/}

                    {/* rute koje ne postoje zato što npr /company uvijek ima /company/users ili slično, ovo su default redirectovi za takve rute */}
                    <Route
                        path={"/company"}
                        element={<Navigate to={AppRoute.COMPANY_SETTINGS}/>}
                    />
                    <Route
                        path={"/webshop"}
                        element={<Navigate to={AppRoute.WEBSHOP_ARTICLES}/>}
                    />

                    {/* WEB DOCUMENTS */}
                    <Route
                        path={AppRoute.WEB_DOCUMENT_NEW_MOVE()}
                        element={<WebDocumentNewMovePage/>}
                    />
                    <Route
                        path={AppRoute.WEB_DOCUMENT_NEW_ORDER()}
                        element={<WebDocumentNewOrderPage/>}
                    />
                    <Route
                        path={AppRoute.WEB_DOCUMENT_MOVE_LIST()}
                        element={<WebDocumentMoveListPage/>}
                    />
                    <Route
                        path={AppRoute.WEB_DOCUMENT_ORDER_LIST()}
                        element={<WebDocumentOrderListPage/>}
                    />
                    <Route
                        path={AppRoute.WEB_DOCUMENT_TMN_LIST()}
                        element={<WebDocumentTMnListPage/>}
                    />
                    <Route
                        path={AppRoute.SEARCH_TICKET_MESSAGES_BY_CONTENT}
                        element={<SearchTicketMessagesByContentPage/>}
                    />

                    <Route path={AppRoute.ARES} element={<AresPage/>}/>
                    <Route path={AppRoute.SQL_TASKS} element={<CompanySqlTasksPage/>}/>

                    <Route
                        path={AppRoute.PLAIN_SQL_TESTING}
                        element={<PlainSQLTestingPage/>}
                    />

                    <Route path={AppRoute.SAVED_FILES} element={<SavedFilesPage/>}/>
                    <Route path={AppRoute.CACHES} element={<CachesPage/>}/>
                    <Route
                        path={AppRoute.LABEL_DEFINITIONS}
                        element={<LabelDefinitionsPage/>}
                    />
                    <Route path={AppRoute.PANTHEON_INIS} element={<PantheonInisPage/>}/>
                    <Route path={AppRoute.PANTHEON} element={<PantheonPage/>}/>
                    <Route
                        path={AppRoute.UPDATE_PANTHEON_INI_FROM_LOCAL}
                        element={<UpdatePantheonIniFromLocalPage/>}
                    />
                    <Route
                        path={AppRoute.ONIX_PHONE_USER_TASKS}
                        element={<OnixPhoneUserTasksPage/>}
                    />
                    <Route
                        path={AppRoute.CODEBOOKS_TIME_LOG_TEMPLATES}
                        element={<CodebooksTimeLogTemplates/>}
                    />
                    <Route
                        path={AppRoute.EMAIL_SETTINGS_TESTING}
                        element={<EmailSettingsTestingPage/>}
                    />
                    <Route
                        path={AppRoute.EMAIL_SETTINGS_TESTING_OVERVIEW()}
                        element={<EmailSettingsTestingOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.WIKI_ENTRY_ADMIN}
                        element={<WikiEntryAdminPage/>}
                    />
                    <Route
                        path={AppRoute.WIKI_ENTRY_OVERVIEW()}
                        element={<WikiEntryOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.WIKI_ENTRY_TAGS_ADMIN}
                        element={<WikiEntryTagsAdminPage/>}
                    />
                    <Route
                        path={AppRoute.WIKI_ENTRY_CATEGORIES_ADMIN}
                        element={<WikiEntryCategoriesAdminPage/>}
                    />
                    <Route
                        path={AppRoute.IBACKUP_ENTRIES}
                        element={<IBackupEntriesPage/>}
                    />
                    <Route
                        path={AppRoute.IBACKUP_COMPUTERS}
                        element={<IBackupComputersPage/>}
                    />
                    <Route
                        path={AppRoute.SQL_BACKUP_OVERVIEW}
                        element={<SqlBackupOverviewPage/>}
                    />
                    <Route
                        path={AppRoute.ALL_COMPANIES_SQL_FORM}
                        element={<AllCompaniesSQLPage/>}
                    />
                    <Route
                        path={AppRoute.TRAVEL_WARRANTS2_OVERVIEW}
                        element={<TravelWarrants2OverviewPage/>}
                    />
                    <Route
                        path={AppRoute.FISCALIZATIONS}
                        element={<FiscalizationsPage/>}
                    />
                    <Route
                        path={AppRoute.CLIENT_SERVERS}
                        element={<ClientServersPage/>}
                    />
                    <Route
                        path={AppRoute.PANTHEON_UPGRADE_LOGS}
                        element={<PantheonUpgradeLogsPage/>}
                    />
                    <Route
                        path={AppRoute.ARES_ACTIVITY_REPORT}
                        element={<AresActivityReportPage/>}
                    />
                    <Route
                        path={AppRoute.ONIX_CLR_USAGE_REPORT}
                        element={<OnixClrUsageReportPage/>}
                    />
                    <Route
                        path={AppRoute.ONIX_DMS_BILLING_REPORT}
                        element={<OnixDmsBillingReportPage/>}
                    />
                    <Route
                        path={AppRoute.ONIX_PHONE_FUNCTIONALITY_ACTIVITY_REPORT}
                        element={<OnixPhoneFunctionalityActivityReportPage/>}
                    />
                    
                    <Route
                        path={AppRoute.ARES_DEPLOYMENTS}
                        element={<AresDeploymentsPage/>}
                    />
                    <Route
                        path={AppRoute.ARES_DEPLOYMENT_GROUP_SNAPSHOTS()}
                        element={<AresDeploymentGroupSnapshotsPage/>}
                    />
                    <Route
                        path={AppRoute.ARES_DEPLOYMENT_GROUP_INSTALLED_COMPANIES()}
                        element={<AresDeploymentGroupInstalledCompaniesPage/>}
                    />
                </Route>
                <Route element={<WikiLayout/>}>
                    <Route
                        path={AppRoute.WIKI_PUBLIC()}
                        element={<WikiMainPublicPage/>}
                    />
                </Route>
            </Routes>
        </Suspense>
    );
};
